import { Alert, Button, Checkbox, Form, Input, InputOtp, type InputProps } from '@heroui/react';
import clsx from 'clsx';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { trackEvent, trackUserLoggedIn } from '@/app/analytics/track.ts';
import DevFastLogin from '@/app/components/dev-fast-login.tsx';
import Logo from '/logo-short.png';
import Background from '/sign-up-background.jpg';
import { useAuth } from '../../hooks/auth-provider';
import { EaseInOutView } from './EaseInOutView';

const inputProps: InputProps = {
  variant: 'flat',
  color: 'default',
  className: 'mb-3',
};

const otpSegmentClassNames = {
  segment:
    'border-2 border-default-500 bg-white text-foreground shadow-sm hover:bg-default-50 data-[active=true]:border-primary-600 data-[has-value=true]:text-foreground',
};

type ViewState = 'startLogin' | 'logInWithOtp' | 'logInWithPassword' | 'setPassword';

export default function LoginPage() {
  const [emailAddress, setEmailAddress] = useState('');
  const [canSetPassword, setCanSetPassword] = useState(false);
  const [view, setView] = useState<ViewState>('startLogin');

  const location = useLocation();
  const navigate = useNavigate();

  function onStartLoginNext(emailAddress: string, logInView: 'logInWithOtp' | 'logInWithPassword') {
    setEmailAddress(emailAddress);
    setView(logInView);

    if (logInView === 'logInWithOtp') {
      setCanSetPassword(true);
    }
  }

  function onLogInWithOtpNext({ setPassword }: { setPassword: boolean }) {
    if (setPassword) {
      setView('setPassword');
    } else {
      navigateAfterLogIn();
    }
  }

  function onLogInWithPasswordSendLoginOtp() {
    setView('logInWithOtp');
    setCanSetPassword(false);
  }

  function navigateAfterLogIn() {
    navigate(location.state?.redirectTo || '/', { replace: true });
  }

  return (
    <>
      <DevFastLogin />
      <div className='flex items-stretch relative w-full flex-1 h-screen bg-gradient-to-bl from-primary-200 via-secondary-200 to-primary-300 p-4 md:p-10'>
        <div className='flex flex-col flex-1 bg-white/100 rounded-3xl md:rounded-r-none backdrop-blur p-3 overflow-y-auto'>
          <img
            src={Logo}
            alt='MeetingMD'
            className='h-auto self-center w-56 object-contain my-10'
          />
          <div className='p-4 flex flex-col self-center w-full max-w-md gap-4 relative overflow-hidden'>
            <EaseInOutView active={view === 'startLogin'}>
              <StartLoginView next={onStartLoginNext} />
            </EaseInOutView>

            <EaseInOutView active={view === 'logInWithOtp'}>
              <LogInWithOtpView
                emailAddress={emailAddress}
                canSetPassword={canSetPassword}
                next={onLogInWithOtpNext}
                back={() => setView('startLogin')}
              />
            </EaseInOutView>

            <EaseInOutView active={view === 'logInWithPassword'}>
              <LogInWithPasswordView
                emailAddress={emailAddress}
                onLogin={navigateAfterLogIn}
                onSendLoginOtp={onLogInWithPasswordSendLoginOtp}
                back={() => setView('startLogin')}
              />
            </EaseInOutView>

            <EaseInOutView active={view === 'setPassword'}>
              <SetPasswordView next={navigateAfterLogIn} />
            </EaseInOutView>
          </div>
        </div>
        <div
          className='flex-col flex-1 hidden md:flex rounded-r-3xl bg-cover bg-top relative'
          style={{ backgroundImage: `url(${Background})` }}
        ></div>
      </div>
    </>
  );
}

function StartLoginView({
  next,
}: {
  next: (emailAddress: string, logInView: 'logInWithOtp' | 'logInWithPassword') => void;
}) {
  const [emailAddress, setEmailAddress] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { startLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await startLogin({ emailAddress });
      if (response.useLoginCode) {
        trackEvent('login_started', { auth_method: 'otp' });
        next(emailAddress, 'logInWithOtp');
      } else if (response.usePassword) {
        trackEvent('login_started', { auth_method: 'password' });
        next(emailAddress, 'logInWithPassword');
      } else {
        setError('Unable to send login code. Please check your email address.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Form onSubmit={onSubmit} className='flex flex-col gap-4' validationBehavior='native'>
      <h1 className='text-xl font-bold self-center mb-3 text-center'>Sign In</h1>
      <Input
        {...inputProps}
        type='email'
        label='Email Address'
        placeholder='Enter your email'
        value={emailAddress}
        onChange={e => setEmailAddress(e.target.value)}
        isRequired
        disabled={isLoading}
      />
      {error && <Alert className='mb-3' color='danger' variant='flat' title={error} />}
      <Button type='submit' size='lg' color='primary' className='w-full' isLoading={isLoading}>
        Submit
      </Button>
      <Button
        size='sm'
        variant='light'
        color='default'
        className='self-center'
        onPress={() => navigate('/onboard', { state: location.state })}
      >
        New rep looking to sign up? Click here
      </Button>
    </Form>
  );
}

function LogInWithOtpView({
  emailAddress,
  canSetPassword,
  next,
  back,
}: {
  emailAddress: string;
  canSetPassword: boolean;
  next: (options: { setPassword: boolean }) => void;
  back: () => void;
}) {
  const [otpCode, setOtpCode] = useState('');
  const [setPassword, setSetPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { logInWithOtp } = useAuth();

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const user = await logInWithOtp({ emailAddress, otpCode });
      if (user) {
        trackUserLoggedIn(user, 'otp');
      }
      next({ setPassword });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to log in. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Form onSubmit={onSubmit} className='flex flex-col gap-4' validationBehavior='native'>
      <h1 className='text-xl font-bold self-center mb-3 text-center'>Verify Your Code</h1>
      <p className='text-center text-sm text-gray-600 mb-2'>
        We've sent a 6-digit code to <strong>{emailAddress}</strong>. Enter it below to log in.
      </p>
      <div className={clsx('flex self-stretch justify-center', { 'mb-3': !canSetPassword })}>
        <InputOtp
          size='lg'
          variant='bordered'
          classNames={otpSegmentClassNames}
          label='Login Code'
          value={otpCode}
          onValueChange={value => setOtpCode(value)}
          length={6}
          isRequired
          disabled={isLoading}
        />
      </div>
      {canSetPassword && (
        <div className='flex self-stretch justify-center mb-3'>
          <Checkbox isSelected={setPassword} onValueChange={setSetPassword}>
            Set a password for your account
          </Checkbox>
        </div>
      )}
      {error && <Alert className='mb-3' color='danger' variant='flat' title={error} />}
      <div className='flex self-stretch gap-3'>
        <Button variant='flat' size='lg' onPress={back} isDisabled={isLoading} className='shrink-0'>
          Back
        </Button>
        <Button type='submit' size='lg' color='primary' isLoading={isLoading} className='flex-1'>
          Log In
        </Button>
      </div>
    </Form>
  );
}

function LogInWithPasswordView({
  emailAddress,
  onLogin,
  onSendLoginOtp,
  back,
}: {
  emailAddress: string;
  onLogin: () => void;
  onSendLoginOtp: () => void;
  back: () => void;
}) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { sendLoginOtp, logInWithPassword } = useAuth();

  const navigate = useNavigate();

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const user = await logInWithPassword({ emailAddress, password });
      if (user) {
        trackUserLoggedIn(user, 'password');
      }
      onLogin();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to log in. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }

  async function handleSendLoginOtp() {
    setError('');
    setIsLoading(true);

    try {
      await sendLoginOtp({ emailAddress });
      onSendLoginOtp();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send login code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }

  async function handlePasswordReset() {
    const params = new URLSearchParams();
    params.append('emailAddress', emailAddress);

    navigate(`/reset-password?${params}`);
  }

  return (
    <Form onSubmit={onSubmit} className='flex flex-col gap-4' validationBehavior='native'>
      <h1 className='text-xl font-bold self-center mb-3 text-center'>Enter Password</h1>
      <Input
        {...inputProps}
        type='password'
        label='Password'
        value={password}
        onChange={e => setPassword(e.target.value)}
        isRequired
        disabled={isLoading}
      />
      {error && <Alert className='mb-3' color='danger' variant='flat' title={error} />}
      <div className='flex self-stretch gap-3'>
        <Button variant='flat' size='lg' onPress={back} isDisabled={isLoading} className='shrink-0'>
          Back
        </Button>
        <Button type='submit' size='lg' color='primary' isLoading={isLoading} className='flex-1'>
          Log In
        </Button>
      </div>
      <Button
        size='lg'
        variant='bordered'
        color='default'
        className='w-full'
        onPress={handleSendLoginOtp}
      >
        Send Login Code via Email
      </Button>
      <Button
        size='sm'
        variant='light'
        color='default'
        className='self-center'
        onPress={handlePasswordReset}
      >
        Reset Password
      </Button>
    </Form>
  );
}

function SetPasswordView({ next }: { next: () => void }) {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPasswordMismatchError, setShowPasswordMismatchError] = useState(false);
  const { setPassword: setAuthPassword } = useAuth();

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (password !== confirmPassword) {
      setShowPasswordMismatchError(true);
      return;
    }

    setError('');
    setIsLoading(true);
    setShowPasswordMismatchError(false);

    try {
      await setAuthPassword({ password });
      trackEvent('password_set');
      next();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }
  return (
    <Form onSubmit={onSubmit} className='flex flex-col gap-4' validationBehavior='native'>
      <h1 className='text-xl font-bold self-center mb-3 text-center'>Set Password</h1>
      <p className='text-center text-sm text-gray-600 mb-2'>
        We now support password login. Please set your password to continue.
      </p>
      <Input
        {...inputProps}
        type='password'
        label='Password'
        value={password}
        onChange={e => setPassword(e.target.value)}
        isRequired
        disabled={isLoading}
      />
      <Input
        {...inputProps}
        type='password'
        label='Confirm Password'
        value={confirmPassword}
        onChange={e => setConfirmPassword(e.target.value)}
        isRequired
        disabled={isLoading}
      />
      {showPasswordMismatchError && password !== confirmPassword && (
        <Alert className='mb-3' color='danger' variant='flat' title='Passwords do not match' />
      )}
      {error && <Alert className='mb-3' color='danger' variant='flat' title={error} />}
      <Button type='submit' size='lg' color='primary' isLoading={isLoading} className='w-full'>
        Set Password and Log In
      </Button>
      <Button size='lg' variant='bordered' color='default' className='w-full' onPress={next}>
        Skip and Log In
      </Button>
    </Form>
  );
}
